// -----------------------------------------------------------------------------
// Subscription plan `type` values and how they map to entitlements
// -----------------------------------------------------------------------------
// Types come straight from the subscription-plans API
// (payments/fetch-subscriptionplans):
//   trial       -> free trial; its own plan, not a short-lived advance. Length
//                  and config live on the hidden 'trial' plan document, which is
//                  marked visible:false so it never reaches the pricing page.
//   advance     -> ADVANCE ALL INDIA, 1 year
//   advancemax  -> ADVANCE ALL INDIA, 2 years
//   standard    -> STANDARD ALL INDIA
//   basic       -> BASIC UPTO 3 STATE
//   pro         -> PRO SINGLE USER ACCESS
//   none        -> no active plan
//
// `advance` and `advancemax` are the same plan on different billing terms: the
// API returns identical feature lists and the same maxUsers for both. Only the
// price, duration and TenderSharkX credit count differ. Every access check must
// therefore treat them as one plan — see normalizePlanType below.
// -----------------------------------------------------------------------------

/** The free trial's plan type. Grants the same features as advance. */
export const TRIAL_PLAN_TYPE = 'trial';

/** Advance billing terms, shortest first. Order drives the pricing toggle. */
export const ADVANCE_VARIANT_TYPES = ['advance', 'advancemax'];

/** The entitlement tier that Advance's billing terms all collapse to. */
export const ADVANCE_ENTITLEMENT_TYPE = 'advance';

export function isAdvanceVariant(type: string): boolean {
  return ADVANCE_VARIANT_TYPES.indexOf((type || '').toLowerCase()) !== -1;
}

/**
 * Collapses a raw plan type to the entitlement it grants, lower-cased.
 *
 * Anything gating access must run the user's plan through this. Without it an
 * `advancemax` customer matches no known plan and falls through to 'none',
 * losing every feature they just paid for.
 *
 * Use the raw type instead only where the billing term itself matters — such as
 * showing which Advance term the user currently owns on the pricing page.
 */
export function normalizePlanType(plan: string | null | undefined): string {
  const value = (plan || '').toLowerCase();
  return isAdvanceVariant(value) ? ADVANCE_ENTITLEMENT_TYPE : value;
}

/**
 * Tiers whose subscription is a running term rather than a stackable allowance.
 *
 * These have nothing to stack — a second one buys the same access twice. BASIC
 * and PRO are absent on purpose: the API records each purchase as its own
 * SubscriptionHistory with its own tender allowance and spends them in turn, so
 * buying again genuinely adds something. PRO gates that on the allowance being
 * spent; see canPurchasePlan.
 */
const NON_STACKABLE_PLAN_TYPES = ['standard'].concat(ADVANCE_VARIANT_TYPES);

/**
 * Plans that grant a spendable allowance rather than a running term, mapped to
 * the field on the active-plan response reporting what is left of it.
 *
 * Re-buying one of these stacks a fresh allowance — the API records each
 * purchase as its own SubscriptionHistory and spends them in turn — so the card
 * reopens once the allowance in hand is used up. PRO spends tender views,
 * BASIC spends state slots.
 */
export const ALLOWANCE_PLAN_FIELDS: { [planType: string]: string } = {
  pro: 'remainingTenderCount',
  basic: 'remainingStateCount',
};

/** The active-plan field carrying `planType`'s allowance, or null if it has none. */
export function remainingAllowanceField(planType: string | null | undefined): string | null {
  return ALLOWANCE_PLAN_FIELDS[(planType || '').toLowerCase()] || null;
}

/**
 * Whether `planType` may be bought while the user holds `userPlan`.
 *
 * Only the card for the plan the user is already on is ever closed off — every
 * other card stays live, so upgrades, downgrades and switching between
 * Advance's 1-year and 2-year terms are all unaffected. Whether that one card
 * is buyable again depends on what the plan is:
 *
 *  - STANDARD / Advance run as a term. Buying the same term twice gains
 *    nothing, so it stays disabled for as long as the plan is live.
 *  - PRO and BASIC grant an allowance. Re-buying stacks a fresh one, which is
 *    the point of those plans — but only once the allowance in hand is spent,
 *    so they unlock at `remainingAllowance` <= 0.
 *  - The free trial is unrestricted.
 *
 * Pass `userPlan` exactly as getRawUserPlan() reports it. That resolves to
 * 'none' the moment the subscription expires, which is what keeps this from
 * blocking an expired holder without a separate expiry check here. It also
 * keeps 'advance' and 'advancemax' distinct, so only the term actually owned
 * is blocked.
 *
 * `remainingAllowance` is whatever remainingAllowanceField() names for the
 * user's own plan. Null means the API did not report one — that is not evidence
 * the allowance is spent, so it leaves the card buyable rather than blocking a
 * legitimate purchase on a reading that never arrived.
 */
export function canPurchasePlan(
  userPlan: string | null | undefined,
  planType: string | null | undefined,
  remainingAllowance: number | null = null
): boolean {
  const target = (planType || '').toLowerCase();
  if (!target) return false;

  const current = (userPlan || '').toLowerCase();
  if (target !== current) return true;

  if (remainingAllowanceField(current)) {
    return remainingAllowance == null || remainingAllowance <= 0;
  }

  return NON_STACKABLE_PLAN_TYPES.indexOf(current) === -1;
}
